import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ClipboardCheck, TriangleAlert } from 'lucide-react';
import { ORDER_TRANSITIONS, canTransition } from '@/lib/domain';
import { OrderStatus, VENUE_EVENTS, type OrderEventPayload } from '@/lib/shared-types';
import { api } from '@/lib/api';
import { useRealtime } from '@/Hooks/useRealtime';
import { usePermission } from '@/Hooks/usePermission';
import { useAppDispatch } from '@/Redux/store';
import {
  applyOrderEvent,
  invalidateBoards,
  invalidateOrder,
  invalidateOrderLists,
  type OrderRow,
} from '@/Redux/Slices/operationsApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { isLapsed, useAwaitingOrders } from './awaitingOrders';
import { apiMessage, formatDuration, formatQuantity } from './shared';
import type { TableLabel } from './waiterPass';

/**
 * The orders a guest placed from the table and nobody has read back yet.
 *
 * WHY THIS EXISTS: a QR order used to go straight to the kitchen. It now stops
 * at CONFIRMED until a waiter reads it back at the table and sends it, which
 * means there is a new way for food never to be cooked — an order that is
 * accepted, paid attention to by nobody, and invisible. The guest's tracking
 * screen says the order is placed; the kitchen has no ticket; nothing on this
 * dashboard was louder about it than a row in a list of two hundred.
 *
 * WHY A SECTION AND NOT A TAB IN `ORDER_TABS`. A tab is the right home for a
 * *place* you go — `tabRoutes.ts` says as much: a tab id is a link, and the two
 * order tabs are two lanes somebody chooses between. This is not a lane, it is
 * an alarm, and the failure it exists to prevent is nobody looking. A third tab
 * is seen only by a person who already went to `/orders` and already suspected
 * something was waiting, and its unread count would sit behind a click on the
 * one screen — the station terminal on the pass wall — that nobody ever
 * navigates. `UnroutedLane` settled the same question the same way and for the
 * same stated reason ("a cook never navigates"): it draws itself above the
 * board on the screen that is already open. So does this, on `/orders`.
 *
 * AND WHY IT IS A TAB ON THE STATION AFTER ALL. `WaiterStation` draws it in a
 * tab panel now, which is the arrangement the paragraph above refuses — with
 * the one condition that paragraph names met, and only because it is met: the
 * count does not go behind the click. It is on the strip whichever panel is
 * open, in a danger tone the moment one of these orders is past the venue's
 * limit, so the number a waiter has to see is exactly as visible as it was when
 * this drew itself above the fold. Both surfaces read the count from
 * `useAwaitingOrders`, so there is one number and one cache entry rather than a
 * badge that can disagree with the list it points at. Take the badge away and
 * this becomes a tab nobody clicks, which is the failure `Kitchen` already
 * shipped once with its dine-in and takeaway tabs.
 *
 * WHY IT IS MOUNTED TWICE. The brief names two readers who are not on the same
 * screen: the waiter station terminal, which watches `WaiterStation` all shift,
 * and a manager, who works in `Orders`. One component in both places rather
 * than two surfaces, because two would be two chances to disagree about which
 * orders are late — and they share one `listOrders` cache entry, so the second
 * mount costs no request.
 *
 * WHY IT DRAWS NOTHING WHEN NOTHING IS WAITING. A venue with
 * `ordering.requireWaiterConfirmation` off answers this read with an empty list
 * forever, and an empty panel permanently pinned to the top of two screens is
 * how a feature nobody enabled becomes clutter everybody learns to scroll past.
 * Silence is the correct output; a failed *check* is not silence, see below.
 *
 * WHAT IT DELIBERATELY DOES NOT DO. Amending an order (`POST /orders/:id/items`
 * and friends) and cancelling it are both real answers to "the guest asked for
 * something else", and neither belongs on a card somebody presses while walking
 * past. Amending needs the whole order, its modifiers and its totals — that is
 * `OrderDetailPanel` — and cancelling destroys a guest's order and wants the
 * reason dialog `Orders` already owns. The same argument `CallCard` makes for
 * keeping Cancel in the drawer. This lane offers the one move a waiter makes
 * without stopping: send it.
 */

/** What `POST /orders/:id/send` needs a permission and a legal transition for. */
const SEND_PERMISSION = 'orders.send';

export function AwaitingConfirmationLane({
  branchId,
  rooms,
  tableLabels,
}: {
  /** The branch whose waiting orders these are. Empty means every branch visible to the actor. */
  branchId?: string;
  /**
   * The rooms to listen in, when the host screen's own branch filter can
   * disagree with the global one. `useRealtime` defaults to the active branch.
   */
  rooms?: string[];
  /**
   * `session.tableId` is a UUID and a UUID is not an instruction. The host
   * passes this when it already holds the floor — `WaiterStation` reads
   * `GET /tables` for the pass — so naming the table costs no second request,
   * and its absence degrades to the order number rather than to a blank.
   */
  tableLabels?: Record<string, TableLabel>;
}) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const dispatch = useAppDispatch();
  const [busy, setBusy] = useState<string | null>(null);

  /**
   * `GET /orders?awaitingConfirmation=true`, longest wait first.
   *
   * The sort is the server's and is not repeated here. It has to be: the wait
   * starts when the order was confirmed, and an order row carries no
   * `confirmedAt` — re-sorting on `createdAt` would be a different ordering
   * wearing the same name, and it would disagree with the backend the first
   * time a draft sat in a cart for ten minutes before the guest sent it.
   *
   * Through `useAwaitingOrders` rather than a `useListOrdersQuery` of its own,
   * because `WaiterStation` puts this count on a tab strip: one set of query
   * arguments is what keeps the badge and these cards on one cache entry.
   */
  const { rows, lapsed: lapsedCount, isLoading, error, now } = useAwaitingOrders(branchId);

  // -------------------------------------------------------------- realtime

  /**
   * An order **joins** this list, and there is nothing local that could know it.
   *
   * `OrderEventPayload` is a status and two ids — no items, no total, no
   * `confirmationDueAt`. There is no row to patch in, so this is the one event
   * here that costs a request, exactly as `ORDER_CREATED` does on `Orders` and
   * `ORDER_READY` does on the pass.
   */
  const onArrived = useCallback(() => {
    dispatch(invalidateOrderLists());
  }, [dispatch]);

  /**
   * And an order leaves, which the payload *can* answer on its own — the row is
   * already here and the only thing that changed is that it stopped waiting. No
   * request: this is the moment a waiter is standing at the table watching the
   * card, and a card that lingers for a round trip is a card they press twice.
   */
  const onLeft = useCallback(
    (payload: OrderEventPayload) => {
      dispatch(applyOrderEvent(payload));
    },
    [dispatch],
  );

  useRealtime<OrderEventPayload>(VENUE_EVENTS.ORDER_CONFIRMED, onArrived, rooms);
  useRealtime<OrderEventPayload>(VENUE_EVENTS.ORDER_SENT_TO_KITCHEN, onLeft, rooms);

  // ---------------------------------------------------------------- action

  /**
   * The write stays on the axios client, as every order write on this dashboard
   * does — `baseApi` says in its own header that it is deliberately not a write
   * path for orders. What it reports afterwards is tags, so the kitchen board
   * that is about to receive these tickets and the order list two screens away
   * both repaint from one call.
   */
  const send = useCallback(
    async (order: OrderRow) => {
      setBusy(order.id);
      try {
        await api.post(`/orders/${order.id}/send`, {});
        toast.success(t('operations.orders.sent'));
        dispatch(invalidateOrder(order.id));
        dispatch(invalidateOrderLists());
        dispatch(invalidateBoards());
      } catch (failure) {
        toast.error(apiMessage(failure, t('operations.errors.actionFailed')));
      } finally {
        setBusy(null);
      }
    },
    [dispatch, t],
  );

  // ---------------------------------------------------------------- render

  /**
   * A failed read is the one thing this lane may not swallow.
   *
   * "Nothing is waiting" and "the check itself is down" would otherwise draw the
   * same empty screen — and the whole reason this surface exists is that an
   * unread order looks exactly like no order at all.
   */
  if (error) {
    return (
      <p
        role="status"
        className="rounded-lg border border-amber-400 bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
      >
        {t('operations.awaiting.checkFailed')}
      </p>
    );
  }

  if (isLoading || !rows.length) return null;

  return (
    <section
      aria-labelledby="awaiting-confirmation"
      className="rounded-xl border border-amber-400 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40"
    >
      <div className="flex flex-wrap items-start gap-2 p-3">
        <ClipboardCheck
          className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-300"
          aria-hidden="true"
        />
        <div className="min-w-0">
          <h2
            id="awaiting-confirmation"
            className="text-sm font-semibold text-amber-900 dark:text-amber-100"
          >
            {/* A count and a sentence are two runs; `bdi` keeps the number from
                dragging the full stop to the wrong end of an Arabic line. */}
            <bdi>{t('operations.awaiting.title', { count: rows.length })}</bdi>
          </h2>
          <p className="text-xs text-amber-800 dark:text-amber-300">
            <bdi>{t('operations.awaiting.hint')}</bdi>
          </p>
        </div>
        {lapsedCount > 0 && (
          <Badge tone="danger" className="ms-auto">
            <bdi>{t('operations.awaiting.lapsedCount', { count: lapsedCount })}</bdi>
          </Badge>
        )}
      </div>

      <ul className="space-y-2 border-t border-amber-200 p-3 dark:border-amber-900">
        {rows.map((order) => (
          <li key={order.id}>
            <WaitingCard
              order={order}
              now={now}
              busy={busy === order.id}
              label={order.session?.tableId ? tableLabels?.[order.session.tableId] : undefined}
              canSend={
                can(SEND_PERMISSION) &&
                canTransition(ORDER_TRANSITIONS, order.status, OrderStatus.SENT_TO_KITCHEN)
              }
              onSend={() => void send(order)}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * One order, as the thing a waiter is about to say out loud.
 *
 * The lines are the point: this card is read back at the table, so every line
 * and its note is on it rather than behind a click.
 *
 * TWO CLOCKS, AND WHY THEY CAN DISAGREE. The loud one counts against
 * `confirmationDueAt` — time left, or time over — because that is the stamp the
 * urgency and the server's ordering both come from, so the top card is always
 * the most urgent one. The quiet one is time since the guest placed the order,
 * which is the question a waiter standing at the table is actually asked. They
 * are not the same measurement: the wait the server sorts on starts when the
 * order was CONFIRMED, and an order row carries no `confirmedAt` — an order
 * that sat in a cart for half an hour before the guest sent it shows a long
 * "waiting" and a short deadline, and that is true rather than a rounding
 * error. Deriving one from the other would mean inventing the SLA here.
 */
function WaitingCard({
  order,
  now,
  busy,
  label,
  canSend,
  onSend,
}: {
  order: OrderRow;
  now: number;
  busy: boolean;
  label: TableLabel | undefined;
  canSend: boolean;
  onSend: () => void;
}) {
  const { t } = useTranslation();
  const lapsed = isLapsed(order.confirmationDueAt, now);
  const waited = Math.max(0, (now - Date.parse(order.createdAt)) / 1000);
  const due = order.confirmationDueAt ? Date.parse(order.confirmationDueAt) : NaN;
  const againstDeadline = Number.isFinite(due) ? Math.abs(now - due) / 1000 : null;

  const heading = label
    ? t('platform.serviceQueue.tableNumber', { number: label.number })
    : (order.customerName ?? t('operations.station.noTable'));

  const where = [label?.areaName, order.number].filter(Boolean).join(' · ');

  return (
    <article className="rounded-lg border border-amber-200 bg-surface p-3 dark:border-amber-900">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold">
            <bdi>{heading}</bdi>
          </h3>
          <p className="truncate text-xs text-ink-muted">
            <bdi>{where}</bdi>
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-0.5">
          <div className="flex items-center gap-2">
            {lapsed && <Badge tone="danger">{t('operations.awaiting.lapsed')}</Badge>}
            {againstDeadline !== null && (
              <span
                className={
                  lapsed
                    ? 'text-sm font-semibold tabular-nums text-red-600 dark:text-red-400'
                    : 'text-sm font-semibold tabular-nums text-accent'
                }
              >
                <bdi>
                  {t(lapsed ? 'operations.awaiting.lateBy' : 'operations.awaiting.dueIn', {
                    duration: formatDuration(againstDeadline),
                  })}
                </bdi>
              </span>
            )}
          </div>
          <span className="text-xs text-ink-muted">
            <bdi>
              {t('operations.awaiting.waitingFor', { duration: formatDuration(waited) })}
            </bdi>
          </span>
        </div>
      </div>

      <ul className="mt-2 space-y-1 border-t pt-2 text-sm">
        {(order.items ?? []).map((item) => (
          <li key={item.id} className="min-w-0">
            {/* `2× Latte` is three runs an Arabic paragraph will reorder into a
                different, plausible, wrong line. Each keeps its own direction. */}
            <bdi className="tabular-nums">{formatQuantity(item.quantity)}×</bdi>{' '}
            <bdi>{item.name}</bdi>
            {!!item.modifiers?.length && (
              <span className="text-ink-muted">
                {' '}
                — {item.modifiers.map((modifier) => modifier.name).join(' · ')}
              </span>
            )}
            {item.notes && (
              <span className="text-amber-700 dark:text-amber-400"> — {item.notes}</span>
            )}
          </li>
        ))}
      </ul>

      {order.notes && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
          <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <bdi>{order.notes}</bdi>
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={!canSend || busy} onClick={onSend}>
          {t('order.sendToKitchen')}
        </Button>
        <span className="text-xs text-ink-muted">
          {canSend
            ? t('operations.awaiting.amendHint')
            : t('operations.kitchen.blocked.permission', { permission: SEND_PERMISSION })}
        </span>
      </div>
    </article>
  );
}
